/**
 * ContentStrategySection — Reuse / Refresh / Generate totals.
 *
 * The three-bucket Content Vault classification. Sprint 3 always
 * shows every asset as "Generate" because no vault exists yet.
 * Sprint 5+ will populate reuse and refresh.
 */
import { Card } from "@/shared/ui/card";
import { RefreshCw, Sparkles, Recycle } from "lucide-react";
import type { ContentStrategyBlock } from "../../types";

export function ContentStrategySection({
  content,
}: {
  content: ContentStrategyBlock;
}): JSX.Element {
  const cards = [
    {
      key: "reuse",
      label: "Reuse",
      count: content.reuse,
      icon: <Recycle className="h-5 w-5 text-emerald-600" aria-hidden />,
      copy: "Assets already in your vault that can be posted as-is.",
      testId: "content-strategy-reuse",
    },
    {
      key: "refresh",
      label: "Refresh",
      count: content.refresh,
      icon: <RefreshCw className="h-5 w-5 text-amber-600" aria-hidden />,
      copy: "Assets that can be lightly rewritten for this campaign.",
      testId: "content-strategy-refresh",
    },
    {
      key: "generate",
      label: "Generate",
      count: content.generate,
      icon: <Sparkles className="h-5 w-5 text-primary" aria-hidden />,
      copy: "Fresh assets Orkexra will create for this campaign.",
      testId: "content-strategy-generate",
    },
  ];

  return (
    <section data-testid="strategy-content-strategy">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Content Strategy
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          Reuse. Refresh. Generate.
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          A total of{" "}
          <span
            className="font-semibold text-foreground"
            data-testid="content-strategy-total"
          >
            {content.total}
          </span>{" "}
          assets are planned for this campaign.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map((c) => (
          <Card key={c.key} className="p-6" data-testid={c.testId}>
            <div className="flex items-center gap-3 mb-3">
              {c.icon}
              <span className="font-semibold">{c.label}</span>
            </div>
            <p className="text-4xl font-semibold tracking-tight mb-3">{c.count}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{c.copy}</p>
          </Card>
        ))}
      </div>

      {!content.vault_ready && (
        <p
          className="mt-4 text-sm text-muted-foreground italic"
          data-testid="content-strategy-vault-note"
        >
          {content.note}
        </p>
      )}
    </section>
  );
}
